import React, { useEffect, useRef, useState } from "react";

const QUANTITY_REQUEST_LOCK_MS = 800;

const QuantitySelector = ({ quantity, className, isPDP = false, allowDecreaseToZero, onQuantityChange, onRemoveCartItem }) => {
    const isDecreaseDisabled = !allowDecreaseToZero && quantity <= 1;
    const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
    const [isQuantityRequestActive, setIsQuantityRequestActive] = useState(false);
    const removeWrapperRef = useRef(null);
    const requestLockTimerRef = useRef(null);

    const lockQuantityRequest = () => {
        setIsQuantityRequestActive(true);
        setIsConfirmingRemove(false);

        if (requestLockTimerRef.current) clearTimeout(requestLockTimerRef.current);
        requestLockTimerRef.current = setTimeout(() => {
            setIsQuantityRequestActive(false);
            requestLockTimerRef.current = null;
        }, QUANTITY_REQUEST_LOCK_MS);
    };

    useEffect(() => () => {
        if (requestLockTimerRef.current) clearTimeout(requestLockTimerRef.current);
    }, []);

    const handleDecreaseQuantity = () => {
        if (quantity > (allowDecreaseToZero ? 0 : 1)) {
            onQuantityChange(quantity - 1, quantity);
            lockQuantityRequest();
        }
    };

    const handleIncreaseQuantity = () => {
        onQuantityChange(quantity + 1, quantity);
        lockQuantityRequest();
    };

    const handleRemoveClick = () => {
        setIsConfirmingRemove((prev) => !prev);
    };

    const handleCancelRemove = () => {
        setIsConfirmingRemove(false);
    };

    const handleConfirmRemove = () => {
        setIsConfirmingRemove(false);
        if (onRemoveCartItem) onRemoveCartItem();
    };

    useEffect(() => {
        if (!isConfirmingRemove) return undefined;

        const handleClickOutside = (event) => {
            if (removeWrapperRef.current && !removeWrapperRef.current.contains(event.target)) {
                setIsConfirmingRemove(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setIsConfirmingRemove(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isConfirmingRemove]);

    return (
        <div
            className={`quant-selector__wrapper  flex text-black w-full items-center select-none leading-4 p-2 text-sm font-medium mx-auto ${isPDP ? "max-w-[5.75rem] border-black border mb-0.5 rounded bg-white" : "max-w-[6.5rem] bg-[#E5EAED] rounded-md my-2.5"} ${className}`}
        >
            <button
                type="button"
                onClick={handleDecreaseQuantity}
                aria-label="Decrease Quantity by 1"
                disabled={isDecreaseDisabled}
                aria-disabled={isDecreaseDisabled}
                className={`${isPDP ? "border-0 font-bold text-lg leading-4" : "border-r border-[#D2D3DC]"} cursor-pointer py-0.5 pr-2 quant-selector__button quant-selector__button--decrease ${isDecreaseDisabled ? "disabled" : ""}`}
            >
                -
            </button>
            <input
                className={`quant-selector__input inline-block w-full border-0 bg-transparent text-small text-center focus-visible:outline-0 ${isPDP && "w-7/12 text-base"}`}
                aria-label="Current Quantity"
                readOnly
                value={quantity}
            />
            <button
                type="button"
                className={`${isPDP ? "border-0 font-bold text-lg leading-4" : "border-l border-[#D2D3DC]"} cursor-pointer py-0.5 pl-2 quant-selector__button`}
                onClick={handleIncreaseQuantity}
                aria-label="Increase Quantity by 1"
            >
                +
            </button>
            {!!onRemoveCartItem && (
                <div ref={removeWrapperRef} className="quant-selector__remove">
                    <button
                        type="button"
                        className={`cart-page__delete-button disabled:opacity-50 disabled:cursor-not-allowed ${isQuantityRequestActive ? "disabled" : ""}`}
                        onClick={handleRemoveClick}
                        disabled={isQuantityRequestActive}
                        aria-disabled={isQuantityRequestActive}
                        aria-label="Remove item from cart"
                        aria-haspopup="dialog"
                        aria-expanded={isConfirmingRemove}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash2-icon lucide-trash-2"><path d="M10 11v6" /><path d="M14 11v6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                    </button>
                    {isConfirmingRemove && (
                        <div
                            role="dialog"
                            aria-label="Confirm remove item"
                            className="quant-selector__confirm-tooltip"
                        >
                            <p className="quant-selector__confirm-title">
                                Remove this item?
                            </p>
                            <p className="quant-selector__confirm-description">
                                This will delete the item from your cart.
                            </p>
                            <div className="quant-selector__confirm-actions">
                                <button
                                    type="button"
                                    onClick={handleCancelRemove}
                                    className="quant-selector__confirm-button quant-selector__confirm-button--cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmRemove}
                                    className="quant-selector__confirm-button quant-selector__confirm-button--confirm"
                                >
                                    Yes, remove
                                </button>
                            </div>
                            <span aria-hidden="true" className="quant-selector__confirm-tooltip-arrow" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuantitySelector;
